import React, {Component} from 'react';
import PropTypes from 'prop-types';

import {geoPath} from 'd3-geo';
import {
  OverlayTrigger,
  Popover,
  Tooltip,
} from 'react-bootstrap';

import './styles.scss';

export default class Choropleth extends Component {

  _createLabels(id, pathGenerator) {
    const {
      data,
      geoJson,
    } = this.props;

    const labels = geoJson.features
      .map((g, i) => {
        const centroid = pathGenerator.centroid(g);

        if (data[g.properties.name]) {
          return (
            <text
              className={id}
              key={`${id}-${i}`}
              x={centroid[0]}
              y={centroid[1]}
            >
              {data[g.properties.name].label}
            </text>
          );
        }
      });

    return labels;
  }

  _createRegions(pathGenerator) {
    const {
      data,
      geoJson,
    } = this.props;

    const regions = geoJson.features
      .map((g, i) => {
        if (data[g.properties.name]) {
          return (
            <OverlayTrigger
              key={`trigger-${i}`}
              overlay={(
                <Popover id='choroplethPopover' title={g.properties.name}>
                  {data[g.properties.name].tooltipText}
                </Popover>
              )}
              placement='top'
            >
              <path
                key={`path-${i}`}
                d={pathGenerator(g)}
                className={`${data[g.properties.name].colourLevel}`}
              />
            </OverlayTrigger>
          );
        } else {
          return (
            <path
              key={`path-${i}`}
              d={pathGenerator(g)}
            />
          );
        }
      });

    return regions;
  }

  _createExtras() {
    const {
      extraPoints,
    } = this.props;

    const extras = extraPoints.map((e, i) => (
      <OverlayTrigger
        key={`extra-point-${i}`}
        overlay={(
          <Popover id='choroplethPopover' title={e.tooltipTitle}>
            {e.tooltipText}
          </Popover>
        )}
        placement='top'
      >
        <g>
          <ellipse
            className={e.colourLevel}
            cx={e.x}
            cy={e.y}
            rx={10}
            ry={10}
          />
          <text
            className={'label'}
            x={e.x + 20}
            y={e.y + 5}
          >
            {e.label}
          </text>
        </g>
      </OverlayTrigger>
    ));

    return extras;
  }

  _createLegend() {
    const {
      dimensions,
      legend,
    } = this.props;
    const {
      colourLevels,
      min,
      max,
      thresholds,
    } = legend;

    const rectWidth = 25;

    return (
      <g transform={`translate(${(dimensions.width / 2) - ((rectWidth * colourLevels.length) / 2)} 10)`}>
        <text x={5} className='label legendMin'>{min}</text>
        <text x={(rectWidth * colourLevels.length) - 5} className='label legendMax'>{max}</text>
        {colourLevels.map((c, i) => (
          <OverlayTrigger
            key={`legend-colour-${i}`}
            overlay={(
              <Tooltip id='choroplethLegendTooltip'>
                {thresholds[i]}
              </Tooltip>
            )}
            placement='top'
          >
            <rect
              className={c}
              x={i * rectWidth}
              y={5}
              width={rectWidth}
              height={rectWidth}
            />
          </OverlayTrigger>
        ))}
      </g>
    );
  }

  render() {
    const {
      dimensions,
      extraPoints,
      geoJson,
      legend,
      mapProjection,
      translate,
    } = this.props;

    const pathGenerator = geoPath().projection(mapProjection);

    const width = (dimensions.width) ? dimensions.width : 0;
    const height = (dimensions.height) ? dimensions.height : 0;
    const translateX = (translate) ? translate.x : 0;
    const translateY = (translate) ? translate.y : 0;

    return (
      <div className={'svgGroup'} role={'img'}>
        <canvas
          className={'choroplethCanvas'}
          width={width}
          height={height}
        >
        </canvas>
        <svg
          className='Choropleth'
          viewBox={`0 0 ${width} ${height}`}
        >
          {legend && this._createLegend()}
          <g transform={`translate(${translateX} ${translateY})`}>
            {geoJson.features && this._createRegions(pathGenerator)}
            {geoJson.features && this._createLabels('label-bg', pathGenerator)}
            {geoJson.features && this._createLabels('label', pathGenerator)}
            {extraPoints && this._createExtras()}
          </g>
        </svg>
      </div>
    );
  }
}

Choropleth.propTypes = {
  data: PropTypes.shape({
    colourLevel: PropTypes.string,
    label: PropTypes.string,
    tooltipText: PropTypes.string,
  }).isRequired,
  dimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  extraPoints: PropTypes.arrayOf(
    PropTypes.shape({
      colourLevel: PropTypes.string,
      label: PropTypes.string,
      tooltipTitle: PropTypes.string,
      tooltipText: PropTypes.string,
      x: PropTypes.number,
      y: PropTypes.number,
    }),
  ),
  geoJson: PropTypes.object.isRequired,
  legend: PropTypes.shape({
    colourLevels: PropTypes.array,
    min: PropTypes.string,
    max: PropTypes.string,
    thresholds: PropTypes.array,
  }),
  mapProjection: PropTypes.func.isRequired,
  translate: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};
